import React from "react";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  GOOGLE_FONTS,
  GOOGLE_FONT_CATEGORIES,
  buildGoogleFontsPreviewUrl,
  findGoogleFont,
  normalizeGoogleFontFamily,
} from "./googleFonts";

const PREVIEW_LINK_SELECTOR = 'link[data-webprofit-google-font-picker="true"]';
const CATEGORY_ORDER = Object.freeze(["sans", "serif", "display", "handwriting", "monospace"]);

function useGoogleFontsPreview() {
  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;
    let link = document.head.querySelector(PREVIEW_LINK_SELECTOR);
    if (!link) {
      link = document.createElement("link");
      link.rel = "stylesheet";
      link.dataset.webprofitGoogleFontPicker = "true";
      link.href = buildGoogleFontsPreviewUrl();
      document.head.appendChild(link);
    }
    return undefined;
  }, []);
}

export default function GoogleFontPicker({
  label,
  value,
  onChange,
  helperText = "Buscá por nombre y elegí viendo una muestra real.",
  disabled = false,
}) {
  useGoogleFontsPreview();
  const selected = findGoogleFont(value);
  const normalizedValue = normalizeGoogleFontFamily(value);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      options={GOOGLE_FONTS}
      value={selected}
      onChange={(_event, option) => {
        if (option?.family) onChange?.(option.family);
      }}
      isOptionEqualToValue={(option, current) => option.family === current?.family}
      getOptionLabel={(option) => option?.family || ""}
      groupBy={(option) => GOOGLE_FONT_CATEGORIES[option.category] || "Otras"}
      filterOptions={(options, state) => {
        const term = String(state.inputValue || "").trim().toLowerCase();
        const filtered = term
          ? options.filter((option) => {
              const category = GOOGLE_FONT_CATEGORIES[option.category] || "";
              return `${option.family} ${category}`.toLowerCase().includes(term);
            })
          : options;
        return [...filtered].sort((first, second) => {
          const categoryDiff = CATEGORY_ORDER.indexOf(first.category) - CATEGORY_ORDER.indexOf(second.category);
          if (categoryDiff) return categoryDiff;
          if (first.recommended !== second.recommended) return first.recommended ? -1 : 1;
          return first.family.localeCompare(second.family, "es");
        });
      }}
      noOptionsText="No encontramos esa tipografía"
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.family} sx={{ py: 1.1, gap: 1.2 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontFamily: `"${option.family}", sans-serif`,
                fontWeight: 700,
                fontSize: 17,
                lineHeight: 1.25,
              }}
            >
              {option.family}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.2,
                color: "rgba(25,36,47,.58)",
                fontFamily: `"${option.family}", sans-serif`,
              }}
            >
              El futuro de tu negocio empieza acá
            </Typography>
          </Box>
          {option.recommended ? (
            <Chip label="Recomendada" size="small" sx={{ fontWeight: 800, height: 24 }} />
          ) : null}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={selected ? helperText : normalizedValue ? `Tipografía actual: ${normalizedValue}. Elegí una de Google Fonts para reemplazarla.` : helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchRoundedIcon sx={{ ml: 0.5, mr: 0.5, color: "rgba(25,36,47,.46)" }} fontSize="small" />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
